import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FUND_OPTIONS, FundProvider } from '@/lib/mock-market-data';
import { Badge } from '@/components/ui/badge';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface FundSelectorProps {
  value: string;
  onChange: (ticker: string) => void;
  compact?: boolean;
}

export function FundSelector({ value, onChange, compact = false }: FundSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Find the selected fund details
  const selectedFund = Object.values(FUND_OPTIONS)
    .flat()
    .find(fund => fund.ticker === value);

  const isTactical = value === 'TACTICAL';

  // Compact mode
  if (compact) {
    return (
      <Select value={value} onValueChange={onChange} open={isOpen} onOpenChange={setIsOpen}>
        <SelectTrigger className="w-full text-sm">
          <SelectValue placeholder="Select a fund">
            {selectedFund && (
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs">{selectedFund.ticker}</span>
                <span className="text-muted-foreground truncate text-xs">{selectedFund.name}</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {(Object.entries(FUND_OPTIONS) as [FundProvider, typeof FUND_OPTIONS[FundProvider]][]).map(
            ([provider, funds]) => (
              <SelectGroup key={provider}>
                <SelectLabel className="flex items-center gap-2 text-xs">
                  {provider}
                  {provider === 'Tactical Management' && (
                    <Badge variant="secondary" className="text-[9px] px-1 py-0">
                      Rec
                    </Badge>
                  )}
                </SelectLabel>
                {funds.map(fund => (
                  <SelectItem key={fund.ticker} value={fund.ticker} className="py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-medium">{fund.ticker}</span>
                      <span className="text-xs truncate">{fund.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectGroup>
            )
          )}
        </SelectContent>
      </Select>
    );
  }

  // Full mode
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-muted-foreground">
          Compare Against
        </label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>Select a fund to compare against your portfolio and the S&P 500 benchmark.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <Select value={value} onValueChange={onChange} open={isOpen} onOpenChange={setIsOpen}>
        <SelectTrigger className="w-full sm:w-[280px]">
          <SelectValue placeholder="Select a fund">
            {selectedFund && (
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{selectedFund.ticker}</span>
                <span className="text-muted-foreground truncate">{selectedFund.name}</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[400px]">
          {(Object.entries(FUND_OPTIONS) as [FundProvider, typeof FUND_OPTIONS[FundProvider]][]).map(
            ([provider, funds]) => (
              <SelectGroup key={provider}>
                <SelectLabel className="flex items-center gap-2">
                  {provider}
                  {provider === 'Tactical Management' && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      Recommended
                    </Badge>
                  )}
                </SelectLabel>
                {funds.map(fund => (
                  <SelectItem key={fund.ticker} value={fund.ticker} className="py-2">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">{fund.ticker}</span>
                        <span className="text-sm">{fund.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{fund.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectGroup>
            )
          )}
        </SelectContent>
      </Select>

      {isTactical && (
        <p className="text-xs text-muted-foreground">
          Tactical funds can convert to cash during market downturns, reducing drawdowns.
        </p>
      )}
    </div>
  );
}
